import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { InputComponentLegacy, ListData } from '@brightside/brightside-ui';
import { SocProfessionListService } from '@brightside-web/desktop/data-access/shared';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/** Narrow shape for profession field config (merged eligibility control). */
export interface ProfessionFieldConfig {
  summaryDisplayValue?: string;
  value?: string;
}

@Component({
  selector: 'brightside-web-profession-search-list',
  templateUrl: './profession-search-list.component.html',
  styleUrls: ['./profession-search-list.component.scss'],
  standalone: false,
})
export class ProfessionSearchListComponent implements OnInit, OnDestroy {
  private readonly _socProfessionListService = inject(SocProfessionListService);
  private _sub = new Subscription();

  @ViewChild(InputComponentLegacy) private _professionSearchInput?: InputComponentLegacy;

  @Input({ required: true }) professionControl!: FormControl<string | null>;
  @Input({ required: true }) controlConfig!: ProfessionFieldConfig;

  searchForm!: FormGroup;
  displayedListItems: ListData[] = [];
  isEmptySearchText = true;
  /** Dropdown is shown only while the search field is focused (after selection or blur it hides). */
  isSearchInputFocused = false;

  get inputText() {
    return this.searchForm?.get('inputText');
  }

  ngOnInit(): void {
    this.searchForm = new FormGroup({
      inputText: new FormControl('', { nonNullable: true }),
    });

    this._sub.add(
      this._socProfessionListService.list$.subscribe(() => {
        const value = this.professionControl.value;
        if (value) {
          const title = this._socProfessionListService.getDisplayTitleFromValue(value);
          if (title) {
            this.inputText?.setValue(title, { emitEvent: false });
          }
        }
        this.refreshDisplayedList(this.inputText?.value ?? '');
      })
    );

    const inputTextControl = this.searchForm.get('inputText');
    if (inputTextControl) {
      this._sub.add(
        inputTextControl.valueChanges
          .pipe(
            debounceTime(250),
            distinctUntilChanged(
              (a, b) => (a ?? '').trim() === (b ?? '').trim()
            )
          )
          .subscribe((newValue) => {
            this.refreshDisplayedList(newValue ?? '');
          })
      );
    }
  }

  private refreshDisplayedList(raw: string): void {
    const trimmed = raw?.trim() ?? '';
    this.isEmptySearchText = !trimmed;
    // Skip Fuse for a single character — still noisy and forces a full index scan.
    if (trimmed.length === 1) {
      this.displayedListItems = [];
      return;
    }
    this.displayedListItems = this._socProfessionListService.searchByProfessionQuery(raw);
  }

  onProfessionClicked(item: ListData): void {
    const submissionValue = this._socProfessionListService.formatProfessionSubmissionValue(
      item.code,
      item.displayName
    );
    this.professionControl.setValue(submissionValue);
    this.professionControl.markAsTouched();
    this.controlConfig.summaryDisplayValue = item.displayName;
    this.controlConfig.value = submissionValue;
    this.inputText?.setValue(item.displayName, { emitEvent: false });
    this._professionSearchInput?.el?.nativeElement?.blur();
    this.isSearchInputFocused = false;
  }

  onSearchInputFocusState(focused: boolean): void {
    this.isSearchInputFocused = focused;
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }
}
